import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import DevNotice from '../components/DevNotice';
import CalendarSidebar from './components/CalendarSidebar';
import MonthView from './components/MonthView';
import ListView from './components/ListView';
import EventDialog from './components/EventDialog';
import { CALENDAR_EVENTS, CALENDAR_GROUPS, VIEW_VALUES } from './mockData';
import { eventColor, periodTitle, shiftPeriod } from './calendarUtils';
import './calendar.css';

// Страница «Календарь» (/calendar). Пока на заглушках из mockData.js —
// вся вёрстка и состояние держатся внутри этой папки.
const CalendarPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';

  const [events, setEvents] = useState(CALENDAR_EVENTS);
  const [current, setCurrent] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [view, setView] = useState('month');
  const [query, setQuery] = useState('');
  const [visibleCalendars, setVisibleCalendars] = useState(() =>
    CALENDAR_GROUPS.flatMap((group) => group.calendars.map((calendar) => calendar.id))
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);

  const colorOf = useCallback((event) => eventColor(CALENDAR_GROUPS, event), []);

  // Фильтр: выключенные календари и поиск по названию.
  const visibleEvents = useMemo(() => {
    const term = query.trim().toLowerCase();
    return events.filter((event) => {
      if (!visibleCalendars.includes(event.calendarId)) return false;
      if (!term) return true;
      return `${event.title} ${event.location || ''}`.toLowerCase().includes(term);
    });
  }, [events, visibleCalendars, query]);

  const toggleCalendar = (calendarId) =>
    setVisibleCalendars((prev) =>
      prev.includes(calendarId) ? prev.filter((id) => id !== calendarId) : [...prev, calendarId]
    );

  const openNewEvent = (date) => {
    setEditingEvent(null);
    setSelectedDate(date || selectedDate);
    setDialogOpen(true);
  };

  const openEvent = (event) => {
    setEditingEvent(event);
    setDialogOpen(true);
  };

  const saveEvent = (payload) => {
    setEvents((prev) => (payload.id
      ? prev.map((event) => (event.id === payload.id ? { ...event, ...payload } : event))
      : [...prev, { ...payload, id: `ev-${Date.now()}` }]));
  };

  const deleteEvent = (id) => setEvents((prev) => prev.filter((event) => event.id !== id));

  const goToday = () => {
    const today = new Date();
    setCurrent(today);
    setSelectedDate(today);
  };

  const handleSelectDate = (day) => {
    setSelectedDate(day);
    setCurrent(day);
  };

  const viewOptions = VIEW_VALUES.map((value) => ({
    value,
    label: t(`calendar.views.${value}`),
    icon: value === 'month' ? 'layers' : 'orders',
  }));
  const viewLabel = t(`calendar.views.${view}`);

  return (
    <div className="cal-page">
      <header className="cal-page-head">
        <h1 className="cal-page-title">{t('calendar.title')}</h1>
        <p className="cal-page-subtitle">{t('calendar.subtitle')}</p>
      </header>

      <DevNotice />

      <div className="cal-shell">
        <CalendarSidebar
          groups={CALENDAR_GROUPS}
          visibleCalendars={visibleCalendars}
          onToggleCalendar={toggleCalendar}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          events={visibleEvents}
          locale={locale}
          onAddEvent={() => openNewEvent(selectedDate)}
          onAddCalendar={() => openNewEvent(selectedDate)}
        />

        <section className="cal-main">
          <header className="cal-toolbar">
            <div className="cal-nav">
              <button
                type="button"
                className="cal-icon-btn bordered"
                onClick={() => setCurrent(shiftPeriod(current, view, -1))}
                title={t('calendar.prev')}
              >
                <Icon name="chevron-left" size={16} />
              </button>
              <button
                type="button"
                className="cal-icon-btn bordered"
                onClick={() => setCurrent(shiftPeriod(current, view, 1))}
                title={t('calendar.next')}
              >
                <Icon name="chevron-right" size={16} />
              </button>
              <button type="button" className="cal-today-btn" onClick={goToday}>
                {t('calendar.today')}
              </button>
              <h2 className="cal-period">{periodTitle(current, view, locale)}</h2>
            </div>

            <div className="cal-toolbar-right">
              <div className="cal-search">
                <Icon name="search" size={16} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('calendar.searchPlaceholder')}
                />
              </div>

              <div className="cal-view-select">
                <button
                  type="button"
                  className="cal-view-trigger"
                  onClick={() => setViewMenuOpen((v) => !v)}
                >
                  <Icon name={view === 'month' ? 'layers' : 'orders'} size={16} />
                  {viewLabel}
                  <Icon name="chevron-down" size={14} />
                </button>
                {viewMenuOpen && (
                  <div className="cal-view-menu">
                    {viewOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={option.value === view ? 'active' : ''}
                        onClick={() => {
                          setView(option.value);
                          setViewMenuOpen(false);
                        }}
                      >
                        <Icon name={option.icon} size={15} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </header>

          {view === 'month' ? (
            <MonthView
              month={current}
              events={visibleEvents}
              selectedDate={selectedDate}
              locale={locale}
              colorOf={colorOf}
              onSelectDate={handleSelectDate}
              onOpenEvent={openEvent}
            />
          ) : (
            <ListView
              events={visibleEvents}
              locale={locale}
              colorOf={colorOf}
              onOpenEvent={openEvent}
            />
          )}
        </section>
      </div>

      <EventDialog
        open={dialogOpen}
        event={editingEvent}
        defaultDate={selectedDate}
        groups={CALENDAR_GROUPS}
        locale={locale}
        onClose={() => setDialogOpen(false)}
        onSave={saveEvent}
        onDelete={deleteEvent}
      />
    </div>
  );
};

export default CalendarPage;
